import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Maximize2, Minimize2, Pause, Play } from "lucide-react";
import { extractFramesFromVideo } from "@/utils/videoFrameExtractor";

interface Exterior360VideoTrimViewLabels {
  trimTitle?: string;
  trimDescription?: string;
  trimGreenRedHint?: string;
  startTime?: string;
  endTime?: string;
  startHelp?: string;
  endHelp?: string;
  selectedLength?: string;
  trimPlayHint?: string;
  extractingFrames?: string;
  generatingTitle?: string;
  generatingStep1Extract?: string;
  extractingCanTakeTime?: string;
  done?: string;
  confirmExtract?: string;
}

interface Exterior360VideoTrimViewProps {
  videoBlob: Blob;
  onFramesExtracted: (frames: Blob[]) => void;
  labels?: Exterior360VideoTrimViewLabels;
}

function formatTime(sec: number): string {
  if (!isFinite(sec) || sec < 0) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function Exterior360VideoTrimView({
  videoBlob,
  onFramesExtracted,
  labels = {},
}: Exterior360VideoTrimViewProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [duration, setDuration] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(0);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState({ done: 0, total: 72 });
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [draggingMarker, setDraggingMarker] = useState<"start" | "end" | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  const [videoUrl, setVideoUrl] = useState<string>("");
  const durationRef = useRef(0);
  durationRef.current = duration;

  useEffect(() => {
    const url = URL.createObjectURL(videoBlob);
    setVideoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [videoBlob]);

  const syncDuration = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const d = video.duration;
    if (isFinite(d) && d > 0) {
      setDuration(d);
      setEndTime((prev) => (prev === 0 ? d : Math.min(prev, d)));
      setStartTime((prev) => (prev === 0 ? 0 : Math.min(prev, d - 0.5)));
    }
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoUrl) return;
    syncDuration();

    const onLoaded = () => syncDuration();

    const onDurationChange = () => syncDuration();

    const onSeeked = () => {
      syncDuration();
      video.currentTime = 0;
      video.removeEventListener("seeked", onSeeked);
    };

    const forceDurationProbe = () => {
      const d = video.duration;
      if (!isFinite(d) || d <= 0) {
        video.currentTime = 1e10;
        video.addEventListener("seeked", onSeeked);
      }
    };

    const onLoadedData = () => {
      onLoaded();
      forceDurationProbe();
    };

    video.addEventListener("loadedmetadata", onLoaded);
    video.addEventListener("loadeddata", onLoadedData);
    video.addEventListener("durationchange", onDurationChange);
    video.addEventListener("canplay", onLoaded);
    return () => {
      video.removeEventListener("loadedmetadata", onLoaded);
      video.removeEventListener("loadeddata", onLoadedData);
      video.removeEventListener("durationchange", onDurationChange);
      video.removeEventListener("canplay", onLoaded);
      video.removeEventListener("seeked", onSeeked);
    };
  }, [videoBlob, videoUrl, syncDuration]);

  const handleSeek = useCallback((e: React.ChangeEvent<HTMLInputElement>, type: "start" | "end") => {
    const val = parseFloat(e.target.value);
    const video = videoRef.current;
    const dur = durationRef.current || 1;
    if (type === "start") {
      const newStart = Math.max(0, Math.min(val, dur - 0.5));
      setStartTime(newStart);
      if (video) {
        video.currentTime = newStart;
        video.pause();
      }
    } else {
      const newEnd = Math.max(0.5, Math.min(val, dur));
      setEndTime(newEnd);
      if (video) {
        video.currentTime = newEnd;
        video.pause();
      }
    }
  }, []);

  const endTimeRef = useRef(0);
  endTimeRef.current = endTime;

  const startTimeRef = useRef(0);
  startTimeRef.current = startTime;

  const handleVideoTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setCurrentTime(video.currentTime);
    const start = startTimeRef.current;
    const end = endTimeRef.current;
    if (end > 0 && video.currentTime >= end - 0.05) {
      video.pause();
      setIsPlaying(false);
      video.currentTime = end;
    }
    if (video.currentTime < start - 0.05) {
      video.currentTime = start;
    }
  }, []);

  const handleVideoPlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.currentTime < startTimeRef.current) {
      video.currentTime = startTimeRef.current;
    }
    if (video.currentTime >= endTimeRef.current - 0.1) {
      video.currentTime = startTimeRef.current;
    }
    setIsPlaying(true);
  }, []);

  const handleVideoPause = useCallback(() => setIsPlaying(false), []);

  const handleVideoSeeked = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const start = startTimeRef.current;
    const end = endTimeRef.current;
    const t = video.currentTime;
    if (end > 0 && t > end + 0.05) {
      video.currentTime = end;
    }
    if (t < start - 0.05) {
      video.currentTime = start;
    }
    setCurrentTime(video.currentTime);
  }, []);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      if (video.currentTime < startTimeRef.current || video.currentTime >= endTimeRef.current - 0.1) {
        video.currentTime = startTimeRef.current;
      }
      video.play();
    } else {
      video.pause();
    }
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  }, []);

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  const handleSeekBarChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    const start = startTimeRef.current;
    const end = endTimeRef.current;
    const clamped = Math.max(start, Math.min(end, val));
    const video = videoRef.current;
    if (video) {
      video.currentTime = clamped;
      setCurrentTime(clamped);
    }
  }, []);

  const updateTimeFromClientX = useCallback((clientX: number, type: "start" | "end") => {
    const track = trackRef.current;
    const dur = durationRef.current;
    const start = startTimeRef.current;
    const end = endTimeRef.current;
    if (!track || dur <= 0) return;
    const rect = track.getBoundingClientRect();
    const fraction = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const time = fraction * dur;
    const video = videoRef.current;
    if (type === "start") {
      const newStart = Math.max(0, Math.min(time, end - 0.5, dur - 0.5));
      setStartTime(newStart);
      if (video) {
        video.currentTime = newStart;
        video.pause();
      }
    } else {
      const newEnd = Math.max(start + 0.5, Math.min(time, dur));
      setEndTime(newEnd);
      if (video) {
        video.currentTime = newEnd;
        video.pause();
      }
    }
  }, []);

  useEffect(() => {
    if (!draggingMarker) return;
    const onMove = (e: PointerEvent) => updateTimeFromClientX(e.clientX, draggingMarker);
    const onUp = () => setDraggingMarker(null);
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
    document.addEventListener("pointercancel", onUp);
    return () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("pointercancel", onUp);
    };
  }, [draggingMarker, updateTimeFromClientX]);

  const handleExtract = useCallback(async () => {
    setIsExtracting(true);
    setError(null);
    setExtractionProgress({ done: 0, total: 72 });
    try {
      const frames = await extractFramesFromVideo(
        videoBlob,
        startTime,
        endTime,
        72,
        (done, total) => setExtractionProgress({ done, total })
      );
      onFramesExtracted(frames);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Kunde inte extrahera bildrutor"
      );
    } finally {
      setIsExtracting(false);
    }
  }, [videoBlob, startTime, endTime, onFramesExtracted]);

  const trimmedDuration = Math.max(0, endTime - startTime);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">
          {labels.trimTitle ?? "Trimma video"}
        </h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          {labels.trimDescription ??
            "Välj start och slut om du vill klippa bort fel i början eller slutet."}
        </p>
        <div className="flex items-center gap-4 mt-2 flex-wrap">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="w-2 h-5 rounded-sm bg-green-500 shrink-0" aria-hidden title={labels.startTime ?? "Startpunkt"} />
            <span>= {labels.startTime ?? "Startpunkt"}</span>
          </span>
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="w-2 h-5 rounded-sm bg-red-500 shrink-0" aria-hidden title={labels.endTime ?? "Slutpunkt"} />
            <span>= {labels.endTime ?? "Slutpunkt"}</span>
          </span>
        </div>
      </div>

      <div ref={containerRef} className="relative rounded-xl overflow-hidden bg-slate-900 aspect-video min-h-[200px] shrink-0">
        <video
          key={videoUrl}
          ref={videoRef}
          src={videoUrl}
          playsInline
          preload="auto"
          className="w-full h-full object-contain outline-none focus:outline-none [&::-webkit-media-controls]:appearance-none [&::-webkit-media-controls-panel]:display-none"
          onTimeUpdate={handleVideoTimeUpdate}
          onPlay={handleVideoPlay}
          onPause={handleVideoPause}
          onSeeked={handleVideoSeeked}
          onLoadedMetadata={() => syncDuration()}
          onDurationChange={() => syncDuration()}
        />
        {videoUrl && !isExtracting && (
          <p className="absolute bottom-14 left-2 text-xs text-white/90 bg-black/50 px-2 py-1 rounded z-10">
            {labels.trimPlayHint ?? "Spela videon · Dra grön/röd markör för att sätta start och slut"}
          </p>
        )}
        {isExtracting && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center rounded-xl bg-slate-900/95 backdrop-blur-sm">
            <Loader2 className="h-12 w-12 text-primary animate-spin mb-3" />
            <p className="text-sm font-semibold text-white mb-1">
              {labels.generatingTitle ?? "Genererar 360-vy"}
            </p>
            <p className="text-xs text-slate-300 mb-2">
              {labels.generatingStep1Extract ?? "Steg 1: Extraherar bildrutor"}{" "}
              {extractionProgress.total > 0
                ? `${Math.round((extractionProgress.done / extractionProgress.total) * 100)}%`
                : ""}
            </p>
            <div className="w-4/5 max-w-[200px] h-2 rounded-full bg-slate-700 overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{
                  width: `${extractionProgress.total > 0 ? (extractionProgress.done / extractionProgress.total) * 100 : 0}%`,
                }}
              />
            </div>
            <p className="text-[10px] text-slate-400 mt-2 px-4 text-center">
              {labels.extractingCanTakeTime ?? "Detta kan ta upp till en minut."}
            </p>
          </div>
        )}
        {/* Custom video controls - constrained to start/end */}
        {videoUrl && (
          <div className="absolute bottom-0 left-0 right-0 z-10 flex items-center gap-2 px-3 py-2 bg-black/70 backdrop-blur-sm rounded-b-xl">
            <button
              type="button"
              onClick={togglePlay}
              className="p-1.5 rounded-full text-white hover:bg-white/20 transition-colors"
              aria-label={isPlaying ? "Pausa" : "Spela"}
            >
              {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </button>
            <button
              type="button"
              onClick={toggleFullscreen}
              className="p-1.5 rounded-full text-white hover:bg-white/20 transition-colors"
              aria-label={isFullscreen ? "Avsluta helskärm" : "Helskärm"}
            >
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
            <span className="text-xs text-white/90 tabular-nums min-w-[4rem]">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
            <div ref={trackRef} className="flex-1 relative h-6 flex items-center group">
              {/* Full-duration track background */}
              <div className="absolute inset-x-0 h-1.5 rounded-full bg-white/30" />
              {/* Start marker line - green, draggable */}
              <div
                role="slider"
                aria-label="Startpunkt"
                aria-valuenow={startTime}
                aria-valuemin={0}
                aria-valuemax={duration}
                tabIndex={0}
                onPointerDown={(e) => {
                  e.preventDefault();
                  (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
                  setDraggingMarker("start");
                }}
                onPointerUp={(e) => {
                  (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
                  setDraggingMarker(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
                    e.preventDefault();
                    const delta = e.key === "ArrowLeft" ? -0.5 : 0.5;
                    handleSeek({ target: { value: String(Math.max(0, Math.min(startTime + delta, duration - 0.5))) } } as React.ChangeEvent<HTMLInputElement>, "start");
                  }
                }}
                className={`absolute top-1/2 w-2 h-6 bg-green-500 rounded-sm z-20 cursor-grab active:cursor-grabbing shadow-sm touch-none select-none ${draggingMarker === "start" ? "ring-2 ring-white/50" : ""}`}
                style={{ left: `${duration > 0 ? (startTime / duration) * 100 : 0}%`, transform: "translate(-50%, -50%)", top: "50%" }}
              />
              {/* End marker line - red, draggable */}
              <div
                role="slider"
                aria-label="Slutpunkt"
                aria-valuenow={endTime}
                aria-valuemin={0}
                aria-valuemax={duration}
                tabIndex={0}
                onPointerDown={(e) => {
                  e.preventDefault();
                  (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
                  setDraggingMarker("end");
                }}
                onPointerUp={(e) => {
                  (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
                  setDraggingMarker(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
                    e.preventDefault();
                    const delta = e.key === "ArrowLeft" ? -0.5 : 0.5;
                    handleSeek({ target: { value: String(Math.max(startTime + 0.5, Math.min(endTime + delta, duration))) } } as React.ChangeEvent<HTMLInputElement>, "end");
                  }
                }}
                className={`absolute top-1/2 w-2 h-6 bg-red-500 rounded-sm z-20 cursor-grab active:cursor-grabbing shadow-sm touch-none select-none ${draggingMarker === "end" ? "ring-2 ring-white/50" : ""}`}
                style={{ left: `${duration > 0 ? (endTime / duration) * 100 : 100}%`, transform: "translate(-50%, -50%)", top: "50%" }}
              />
              <input
                type="range"
                min={startTime}
                max={Math.max(startTime + 0.01, endTime)}
                step={0.1}
                value={Math.max(startTime, Math.min(endTime, currentTime))}
                onChange={handleSeekBarChange}
                className="absolute inset-0 w-full h-full appearance-none bg-transparent cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:cursor-pointer"
              />
            </div>
          </div>
        )}
      </div>

      <p className="text-sm text-muted-foreground font-medium">
        {labels.selectedLength ?? "Vald längd"}: {formatTime(trimmedDuration)} → 72 bildrutor
      </p>

      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      <Button
        onClick={handleExtract}
        disabled={isExtracting || trimmedDuration < 0.5}
        size="lg"
        className="w-full h-11 rounded-xl font-semibold"
      >
        {isExtracting ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            {labels.extractingFrames ?? "Extraherar 72 bildrutor…"}
          </>
        ) : (
          <>
            <Check className="h-4 w-4 mr-2" />
            {labels.confirmExtract ?? "Skapa 360-vy"}
          </>
        )}
      </Button>
    </div>
  );
}
